import React from 'react';
import { ResultComponentProps } from '../../common/types';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { Card, Typography, Table, Tag, Space, Divider, Alert, Slider } from 'antd';
import ReactECharts from 'echarts-for-react';

const { Title, Text, Paragraph } = Typography;

interface DisorderData {
  sequence_id: string;
  sequence: string;
  disorder_scores: number[];
  avg_disorder: number;
  disordered_regions: Array<{
    start: number;
    end: number;
    length: number;
    avg_score: number;
  }>;
}

// 计算无序区域的辅助函数
const computeDisorderedRegionsHelper = (scores: number[], threshold: number) => {
  const regions: Array<{start: number; end: number; length: number; avg_score: number}> = [];
  let start = -1;

  for (let i = 0; i < scores.length; i++) {
    if (scores[i] > threshold) {
      if (start === -1) start = i;
    } else {
      if (start !== -1) {
        const end = i - 1;
        const region_scores = scores.slice(start, i);
        const avg_score = region_scores.reduce((sum, score) => sum + score, 0) / region_scores.length;
        regions.push({
          start: start + 1, // 1-based indexing
          end: end + 1,
          length: end - start + 1,
          avg_score
        });
        start = -1;
      }
    }
  }

  // 处理最后一个区域
  if (start !== -1) {
    const end = scores.length - 1;
    const region_scores = scores.slice(start);
    const avg_score = region_scores.reduce((sum, score) => sum + score, 0) / region_scores.length;
    regions.push({
      start: start + 1,
      end: end + 1,
      length: end - start + 1,
      avg_score
    });
  }

  return regions;
};

// 单个序列结果组件
const DisorderSequenceResult: React.FC<{
  result: any;
  artifacts: any;
  sequenceId: string;
}> = ({ result, artifacts, sequenceId }) => {
  const [threshold, setThreshold] = React.useState(0.5);
  
  const disorderScore = result.disorder_score || result.avg_disorder || 0;
  const disorderRegions = result.disorder_regions || [];
  const sequence = result.sequence || '';
  
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card size="small">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>序列长度: </Text>
            <Text>{sequence.length} 氨基酸</Text>
          </div>
          <div>
            <Text strong>平均无序度: </Text>
            <Text>{typeof disorderScore === 'number' ? disorderScore.toFixed(3) : disorderScore}</Text>
          </div>
          <div>
            <Text strong>无序区域数量: </Text>
            <Text>{disorderRegions.length}</Text>
          </div>
        </Space>
      </Card>

      {disorderRegions.length > 0 && (
        <>
          <Title level={5}>无序区域</Title>
          <Table 
            dataSource={disorderRegions} 
            columns={[
              {
                title: '起始位置',
                dataIndex: 'start',
                key: 'start',
              },
              {
                title: '结束位置',
                dataIndex: 'end',
                key: 'end',
              },
              {
                title: '长度',
                key: 'length',
                render: (_: any, record: any) => record.end - record.start + 1
              },
              {
                title: '平均得分',
                dataIndex: 'avg_score',
                key: 'avg_score',
                render: (score: number) => score?.toFixed(3) || 'N/A'
              }
            ]}
            rowKey={(record, index) => `${record.start}-${record.end}-${index}`}
            pagination={false}
            size="small"
          />
        </>
      )}
    </Space>
  );
};

// 无序区域预测结果展示组件
const DisorderPredictionResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults = false
}) => {
  const [threshold, setThreshold] = React.useState(0.5);
  
  console.log('🔍 DisorderPredictionResult 接收到的数据:', {
    data,
    hasData: !!data,
    dataKeys: data ? Object.keys(data) : [],
    hasResults: !!(data && data.results),
    resultsLength: data?.results ? data.results.length : 0
  });

  // 如果没有数据，显示警告
  if (!data) {
    return (
      <BaseResultDisplay
        data={data}
        title={title || "无序区域预测结果"}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
      >
        <Text type="warning">无法显示无序区域预测结果，数据为空。</Text>
      </BaseResultDisplay>
    );
  }

  // 处理新的统一数据格式
  const processedData = React.useMemo(() => {
    // 处理新的统一格式：{ results: [...], artifacts: {...} }
    if (data && data.results && Array.isArray(data.results)) {
      return {
        isUnified: true,
        results: data.results,
        artifacts: data.artifacts || {},
        meta: data.meta || {},
        total_sequences: data.total_sequences || data.results.length,
        successful_count: data.successful_count || 0,
        failed_count: data.failed_count || 0
      };
    }
    
    // 兼容旧格式
    return { isUnified: false, ...data };
  }, [data]);

  // 对于统一格式，直接转换为 parsedData 格式，使用完整的可视化功能
  // 不再使用简化的 DisorderSequenceResult

  // 解析新的统一数据格式
  const parsedData = React.useMemo(() => {
    // 处理新的统一格式：{ results: [...], artifacts: { result_content } }
    if (data && data.results && Array.isArray(data.results)) {
      // 优先：若顶层 artifacts.result_content 存在，按多行CSV一次性解析，避免重复
      const globalContent: string | undefined = typeof data.artifacts?.result_content === 'string'
        ? data.artifacts.result_content as string
        : undefined;

      const parseCsvLines = (content: string): DisorderData[] => {
        const lines = content.trim().split('\n').map((s: string) => s.trim()).filter(Boolean);
        const out: DisorderData[] = [];
        for (const line of lines) {
          const parts = line.split(',').map((p: string) => p.trim());
          if (parts.length < 3) continue;
          const id = parts[0];
          const sequence = parts[1];
          const scores: number[] = parts.slice(2).map((x: string) => parseFloat(x)).filter((n: number) => !isNaN(n));
          if (scores.length === 0) continue;
          const avg_disorder = scores.reduce((sum, s) => sum + s, 0) / scores.length;
          const disordered_regions = computeDisorderedRegionsHelper(scores, 0.5);
          out.push({
            sequence_id: id,
            sequence,
            disorder_scores: scores,
            avg_disorder,
            disordered_regions
          });
        }
        return out;
      };

      if (globalContent && globalContent.trim()) {
        const parsed = parseCsvLines(globalContent);
        if (parsed.length > 0) {
          console.log(`✅ 通过全局artifacts.result_content 解析出 ${parsed.length} 条记录`);
          return parsed;
        }
      }

      // 其次：逐项解析（支持 result.raw_data / result.result_content 等），并尝试按索引对齐
      const results: DisorderData[] = [];
      data.results.forEach((item: any, index: number) => {
        if (!item?.success || !item?.result) return;
        const result = item.result || {};

        try {
          // 结构化数据
          if (Array.isArray(result.disorder_scores)) {
            const scores: number[] = result.disorder_scores as number[];
            const avg_disorder = result.avg_disorder || (scores.reduce((sum: number, s: number) => sum + s, 0) / scores.length);
            results.push({
              sequence_id: item.sequence_id || result.sequence_id || `序列${index + 1}`,
              sequence: result.sequence || '',
              disorder_scores: scores,
              avg_disorder,
              disordered_regions: result.disorder_regions || computeDisorderedRegionsHelper(scores, 0.5)
            });
            return;
          }

          // CSV文本：优先使用 result.raw_data；否则退回 result.result_content / item.artifacts.result_content
          const csvText: string | undefined =
            (typeof result.raw_data === 'string' && result.raw_data.trim() ? result.raw_data : undefined) ||
            (typeof result.result_content === 'string' && result.result_content.trim() ? result.result_content : undefined) ||
            (typeof item.artifacts?.result_content === 'string' && item.artifacts.result_content.trim() ? item.artifacts.result_content : undefined);

          if (csvText) {
            const lines = csvText.trim().split('\n').map((s: string) => s.trim()).filter(Boolean);
            if (lines.length > 0) {
              // 尝试按索引对齐，避免三条结果均取第一行
              const line = lines[Math.min(index, lines.length - 1)];
              const parts = line.split(',').map((p: string) => p.trim());
              if (parts.length >= 3) {
                const id = parts[0] || item.sequence_id || `序列${index + 1}`;
                const sequence = parts[1];
                const scores: number[] = parts.slice(2).map((x: string) => parseFloat(x)).filter((n: number) => !isNaN(n));
                if (scores.length > 0) {
                  const avg_disorder = scores.reduce((sum, s) => sum + s, 0) / scores.length;
                  const disordered_regions = computeDisorderedRegionsHelper(scores, 0.5);
                  results.push({
                    sequence_id: id,
                    sequence,
                    disorder_scores: scores,
                    avg_disorder,
                    disordered_regions
                  });
                  return;
                }
              }
            }
          }
        } catch (err) {
          console.error(`解析无序结果第${index + 1}项失败:`, err);
        }
      });

      return results;
    }
    
    // 兼容旧格式
    if (data && data.result_content) {
      const lines = data.result_content.trim().split('\n');
      if (lines.length < 1) return [];

      const results: DisorderData[] = [];
      const sequence_id = title || 'protein_sequence';

      const firstLine = lines[0];
      const parts = firstLine.split(',').map((part: string) => part.trim());
      
      if (parts.length < 3) return [];
      
      const id = parts[0];
      const sequence = parts[1];
      const scores: number[] = parts.slice(2).map((score: string) => parseFloat(score));

      if (scores.length === 0) return [];

      const avg_disorder = scores.reduce((sum, score) => sum + score, 0) / scores.length;

      const disordered_regions: Array<{start: number; end: number; length: number; avg_score: number}> = [];
      let start = -1;

      for (let i = 0; i < scores.length; i++) {
        if (scores[i] > 0.5) {
          if (start === -1) start = i;
        } else {
          if (start !== -1) {
            const end = i - 1;
            const region_scores = scores.slice(start, i);
            const avg_score = region_scores.reduce((sum, score) => sum + score, 0) / region_scores.length;
            disordered_regions.push({
              start: start + 1,
              end: end + 1,
              length: end - start + 1,
              avg_score
            });
            start = -1;
          }
        }
      }

      if (start !== -1) {
        const end = scores.length - 1;
        const region_scores = scores.slice(start);
        const avg_score = region_scores.reduce((sum, score) => sum + score, 0) / region_scores.length;
        disordered_regions.push({
          start: start + 1,
          end: end + 1,
          length: end - start + 1,
          avg_score
        });
      }

      results.push({
        sequence_id: id,
        sequence,
        disorder_scores: scores,
        avg_disorder,
        disordered_regions
      });

      return results;
    }

    return [];
  }, [data, title]);

  // 根据阈值计算无序区域
  const computeDisorderedRegions = (scores: number[], th: number) => {
    const regions: Array<{start: number; end: number; length: number; avg_score: number}> = [];
    let start = -1;
    for (let i = 0; i < scores.length; i++) {
      if (scores[i] > th) {
        if (start === -1) start = i;
      } else {
        if (start !== -1) {
          const end = i - 1;
          const region_scores = scores.slice(start, i);
          const avg_score = region_scores.reduce((sum, s) => sum + s, 0) / region_scores.length;
          regions.push({
            start: start + 1,
            end: end + 1,
            length: end - start + 1,
            avg_score
          });
          start = -1;
        }
      }
    }
    if (start !== -1) {
      const end = scores.length - 1;
      const region_scores = scores.slice(start);
      const avg_score = region_scores.reduce((sum, s) => sum + s, 0) / region_scores.length;
      regions.push({
        start: start + 1,
        end: end + 1,
        length: end - start + 1,
        avg_score
      });
    }
    return regions;
  };

  // 生成ECharts配置
  const getChartOption = (sequenceData: DisorderData) => {
    const chartData = sequenceData.disorder_scores.map((score, index) => [index + 1, score]);
    
    return {
      title: {
        text: '无序度分布曲线',
        left: 'center',
        textStyle: {
          fontSize: 14
        }
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const point = params[0];
          return `位置: ${point.data[0]}<br/>无序度评分: ${point.data[1].toFixed(3)}`;
        }
      },
      xAxis: {
        type: 'value',
        name: '氨基酸位置',
        nameLocation: 'middle',
        nameGap: 30
      },
      yAxis: {
        type: 'value',
        name: '无序度评分',
        min: 0,
        max: 1,
        nameLocation: 'middle',
        nameGap: 40
      },
      series: [
        {
          data: chartData,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 3,
          lineStyle: {
            width: 2
          },
          itemStyle: {
            color: (params: any) => params.data[1] > threshold ? '#ff4d4f' : '#1890ff'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0,
                color: 'rgba(255, 77, 79, 0.3)' // 红色半透明
              }, {
                offset: 0.5,
                color: 'rgba(255, 77, 79, 0.1)' // 红色更透明
              }, {
                offset: 1,
                color: 'rgba(24, 144, 255, 0.1)' // 蓝色半透明
              }]
            }
          }
        }
      ],
      grid: {
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: '15%'
      },
      visualMap: {
        show: false,
        dimension: 1,
        pieces: [{
          gt: threshold,
          lte: 1,
          color: '#ff4d4f'
        }, {
          gt: 0,
          lte: threshold,
          color: '#1890ff'
        }]
      },
      // 添加阈值线
      markLine: {
        silent: true,
        data: [
          {
            yAxis: threshold,
            lineStyle: {
              color: '#ff4d4f',
              type: 'dashed',
              width: 2
            },
            label: {
              formatter: `无序阈值 (${threshold.toFixed(2)})`,
              position: 'middle',
              distance: 10
            }
          }
        ]
      }
    };
  };

  // 无序区域表格列定义
  const disorderColumns = [
    {
      title: '起始位置',
      dataIndex: 'start',
      key: 'start',
    },
    {
      title: '结束位置',
      dataIndex: 'end',
      key: 'end',
    },
    {
      title: '长度',
      dataIndex: 'length',
      key: 'length',
    },
    {
      title: '平均无序度',
      dataIndex: 'avg_score',
      key: 'avg_score',
      render: (score: number) => (
        <Tag color={score > 0.7 ? 'red' : score > 0.6 ? 'orange' : 'yellow'}>
          {score.toFixed(3)}
        </Tag>
      ),
    },
  ];

  // 渲染带颜色的氨基酸序列
  const renderColoredSequence = (sequence: string, scores: number[]) => {
    if (!sequence || !scores || sequence.length !== scores.length) {
      return <Text code>序列长度与评分数量不匹配</Text>;
    }

    return (
      <div style={{ fontFamily: 'monospace', fontSize: '14px', lineHeight: '1.8', overflowX: 'auto', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        {sequence.split('').map((aa, index) => {
          const score = scores[index];
          const isDisordered = score > threshold;
          return (
            <span 
              key={index} 
              style={{ 
                color: isDisordered ? '#ff4d4f' : '#1890ff',
                fontWeight: isDisordered ? 'bold' : 'normal',
                backgroundColor: isDisordered ? 'rgba(255, 77, 79, 0.1)' : 'transparent',
                padding: '2px 0',
                borderRadius: '2px'
              }}
              title={`位置: ${index + 1}, 氨基酸: ${aa}, 无序度: ${score.toFixed(3)}`}
            >
              {aa}
            </span>
          );
        })}
      </div>
    );
  };

  // 渲染阈值条
  const renderThresholdBar = () => {
    return (
      <div style={{ marginTop: '10px', marginBottom: '20px', padding: '0 16px' }}>
        <Slider
          min={0}
          max={1}
          step={0.01}
          value={threshold}
          onChange={(value) => setThreshold(value)}
          tooltip={{ formatter: (val?: number) => `阈值: ${(val ?? 0).toFixed(2)}` }}
          railStyle={{
            height: 20,
            background: 'linear-gradient(to right, #1890ff, #ff4d4f)',
            borderRadius: 4
          }}
          trackStyle={{ background: 'transparent' }}
          handleStyle={{
            border: 'none',
            height: 40,
            width: 2,
            marginTop: -10,
            background: '#000'
          }}
          marks={{
            0: '0',
            1: '1'
          }}
        />
      </div>
    );
  };

  console.log('🔍 DisorderPredictionResult 最终渲染:', {
    parsedDataLength: parsedData.length,
    parsedData: parsedData,
    threshold: threshold
  });

  return (
    <BaseResultDisplay
      title={title || '蛋白质无序区域预测结果'}
      data={data}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
    >
      <Alert
        message="无序区域预测说明"
        description="无序度评分范围为0-1，评分>0.5的区域被认为是无序区域。红色表示无序区域，蓝色表示有序区域。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <div style={{ marginBottom: 16, padding: 8, background: '#f0f0f0', borderRadius: 4 }}>
        <strong>调试信息:</strong> 解析到 {parsedData.length} 个序列结果
      </div>
      
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {parsedData.length > 0 ? parsedData.map((sequenceData, index) => (
          <Card key={index} size="small" title={`序列: ${sequenceData.sequence_id}`}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* 基本信息 */}
              <div>
                <Text strong>序列长度: </Text>
                <Text>{sequenceData.sequence.length} 个氨基酸</Text>
                <Divider type="vertical" />
                <Text strong>平均无序度: </Text>
                <Tag color={sequenceData.avg_disorder > threshold ? 'red' : 'green'}>
                  {sequenceData.avg_disorder.toFixed(3)}
                </Tag>
                <Divider type="vertical" />
                <Text strong>无序区域数量: </Text>
                <Text>{sequenceData.disordered_regions.length}</Text>
              </div>

              {/* 阈值条 */}
              {renderThresholdBar()}

              {/* 无序度曲线图 */}
              <div>
                <ReactECharts 
                  option={getChartOption(sequenceData)} 
                  style={{ height: '300px' }}
                />
              </div>

              {/* 带颜色的氨基酸序列 */}
              <div>
                <Title level={5}>序列信息 (红色表示无序区域)</Title>
                {renderColoredSequence(sequenceData.sequence, sequenceData.disorder_scores)}
              </div>

              {/* 无序区域详情 */}
              {computeDisorderedRegions(sequenceData.disorder_scores, threshold).length > 0 && (
                <div>
                  <Title level={5}>识别的无序区域</Title>
                  <Table
                    columns={disorderColumns}
                    dataSource={computeDisorderedRegions(sequenceData.disorder_scores, threshold).map((region, idx) => ({
                      ...region,
                      key: idx
                    }))}
                    pagination={false}
                    size="small"
                  />
                </div>
              )}
            </Space>
          </Card>
        )) : (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Text type="secondary">没有解析到有效的序列数据</Text>
          </div>
        )}
      </Space>
    </BaseResultDisplay>
  );
};

export default DisorderPredictionResult;